import React, { useState, useEffect, useRef } from 'react';
import { Provider, useSelector, useDispatch } from 'react-redux';
import { togglePlayPause } from '../../store/slices/playbackSlice';
import { setIsFullscreen } from '../../store/slices/editorUISlice';
import { VideoRenderer, ImageRenderer, DocumentRenderer, QuestionRenderer, TextRenderer, AssignedSectionRenderer } from '../../components/PreviewRenderers';
import { selectAllAssets } from '../../store/slices/assetsSlice';
import { selectTotalDuration } from '../../store/slices/timelineSlice';
import SeekingBar from '../../components/SeekingBar';
import closeIcon from '../../../../assets/images/closeIcon.svg';
import classes from './styles.module.scss';

function PreviewContent() {
  const dispatch = useDispatch();
  const [showControls, setShowControls] = useState(true);
  const hideTimerRef = useRef(null);
  const fullscreenContainerRef = useRef();

  const timelineItems = useSelector((state) => state.timeline.items);
  const allAssets = useSelector(selectAllAssets);
  const totalDuration = useSelector(selectTotalDuration);
  const isPlaying = useSelector((state) => state.playback.isPlaying);
  const currentTime = useSelector((state) => state.playback.currentTime);

  // Auto-hide playback controls after inactivity
  const scheduleHide = () => {
    if (hideTimerRef.current) {
      window.clearTimeout(hideTimerRef.current);
    }
    hideTimerRef.current = window.setTimeout(() => setShowControls(false), 5000);
  };

  const handleUserActivity = () => {
    if (!showControls) setShowControls(true);
    scheduleHide();
  };

  useEffect(() => {
    scheduleHide();
    return () => {
      if (hideTimerRef.current) {
        window.clearTimeout(hideTimerRef.current);
      }
    };
  }, []);

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      dispatch(setIsFullscreen(!!document.fullscreenElement));
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, [dispatch]);

  // Spacebar to play/pause
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (
        (e.key === ' ' || e.code === 'Space') &&
        !(e.target instanceof HTMLInputElement) &&
        !(e.target instanceof HTMLTextAreaElement)
      ) {
        e.preventDefault();
        dispatch(togglePlayPause());
        handleUserActivity();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [dispatch]);

  // Find current item based on currentTime
  const effectiveTime = isPlaying
    ? currentTime
    : Math.max(0, currentTime - 0.001);

  const currentItem =
    timelineItems.find((item) => {
      const itemEnd = item.start_time + item.duration;
      return effectiveTime >= item.start_time && effectiveTime < itemEnd;
    }) ||
    (timelineItems.length > 0 ? timelineItems[timelineItems.length - 1] : null);

  const currentAsset = currentItem
    ? allAssets.find((a) => a.id === currentItem.asset_id)
    : null;

  const renderPreview = () => {
    if (!currentAsset || !currentItem) {
      return (
        <div style={{ textAlign: 'center', color: '#ffffff' }}>
          <div style={{ fontSize: 18, fontWeight: 600, color: '#e5e7eb' }}>
            No Content
          </div>
          <div style={{ fontSize: 13, marginTop: 6, color: '#9ca3af' }}>
            Timeline is empty
          </div>
        </div>
      );
    }

    if (currentAsset.type === 'video') {
      return <VideoRenderer asset={currentAsset} currentItem={currentItem} />;
    }

    const props = {
      asset: currentAsset,
      currentItem,
    };
    if (currentAsset.type === 'image') return <ImageRenderer {...props} />;
    if (currentAsset.type === 'document')
      return <DocumentRenderer {...props} />;
    if (currentAsset.type === 'question')
      return <QuestionRenderer {...props} />;
    if (currentAsset.type === 'text')
      return <TextRenderer {...props} />;
    if (currentAsset.type === 'assigned-section')
      return <AssignedSectionRenderer {...props} />;
    return <div>Unknown asset type</div>;
  };

  return (
    <div className={classes.previewContainer}>
      {/* Preview Area */}
      <div
        ref={fullscreenContainerRef}
        className={classes.previewArea}
        onMouseMove={handleUserActivity}
        onTouchStart={handleUserActivity}
      >
        <div className={classes.previewContent}>
          {renderPreview()}
        </div>

        {/* Seeking bar at bottom */}
        {totalDuration > 0 && (
          <div className={classes.seekingBarContainer}>
            <SeekingBar fullscreenContainerRef={fullscreenContainerRef} />
          </div>
        )}
      </div>
    </div>
  );
}

export default function ProductionPreviewModal({ show, handleClose, store }) {
  // ESC to close
  useEffect(() => {
    if (!show) return undefined;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        // Exit fullscreen if in fullscreen mode
        if (document.fullscreenElement) {
          document.exitFullscreen();
        }
        handleClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [show, handleClose]);

  // Exit fullscreen when modal closes
  useEffect(() => {
    if (!show && document.fullscreenElement) {
      document.exitFullscreen();
    }
  }, [show]);

  if (!show) return null;

  return (
    <div className={classes.backdrop} onClick={handleClose}>
      <div className={classes.modal} onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className={classes.closeButton}
          onClick={handleClose}
          aria-label="Close"
        >
          <img src={closeIcon} alt="Close" />
        </button>
        <Provider store={store}>
          <PreviewContent />
        </Provider>
      </div>
    </div>
  );
}

